import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';
import FileSaver from 'filesaver.js';

import * as api from '../lib/api';
import {withUser} from '../lib/user';
import withUrlState from '../lib/with-url-state';
import Screen from '../components/Screen';
import Tabs from '../components/Tabs';
import Content from '../components/Content';
import Card from '../components/Card';
import Button from '../components/Button';
import Loading from '../components/Loading';
import {default as ErrorMessage} from '../components/Error';
import Icon from '../components/Icon';
import Menu from '../components/Menu';
import ReportParametersModal from '../components/ReportParametersModal';
import Role from "../lib/Role";

class ReportViewerScreen extends PureComponent {
  viewer = React.createRef();
  state = {
    loading: true,
    error: false,
    report: null,
    catalog: [],
    optionsOpen: false,
    downloading: false,
    downloadMenuOpen: false,
    editParameters: false
  };

  componentDidMount() {
    const {urlState, onUrlStateChange} = this.props;
    this.runReport(urlState);

    // If the URL changes, re-run the report.
    onUrlStateChange(urlState => this.runReport(urlState));
  }

  async runReport(parameters) {
    const {credentials, match: {params: {id}}} = this.props;

    this.setState({loading: true, error: false});

    try {
      const [reports, catalog] = await Promise.all([
        api.getReports(credentials),
        api.getCatalog(credentials)
      ]);
      const report = reports.filter(r => r.id === id)[0];

      if (!report) {
        throw new Error('Report not found with ID=' + id);
      }

      // Start running the report.
      const content = await api.runHtmlReport(report.id, parameters, credentials);

      this.setState({report, catalog, loading: false}, () => {
        const handle = this.viewer.current;
        handle.innerHTML = content;
      });
    }
    catch (e) {
      if (e.status && e.status === 204) {
        this.setState({error: 'no-data', loading: false});
      }
      else {
        this.setState({error: 'general', loading: false});
      }
    }
  }

  toggleReportOptions = () => {
    this.setState(prevState => ({
      optionsOpen: !prevState.optionsOpen
    }));
  }

  toggleParameters = () => {
    this.setState(prevState => ({
      editParameters: !prevState.editParameters
    }));
  }

  toggleDownloads = () => {
    this.setState(prevState => ({
      downloadMenuOpen: !prevState.downloadMenuOpen
    }))
  }

  downloadReport = async (format) => {
    const {credentials, urlState} = this.props;
    const {report} = this.state;
    const now = new Date();

    this.setState({downloading: true});
    try {
      let content;
      switch(format) {
        case 'pdf':
          content = await api.runPdfReport(report.id, urlState, credentials);
          break;
        case 'xlsx':
          content = await api.runXlsxReport(report.id, urlState, credentials);
          break;
        default:
          throw new Error("invalid Format");
      }

      FileSaver.saveAs(content, `${report.name}-${now.getTime()}.${format}`);
    }
    catch(e) {
      NotificationManager.error(`Unable to export to ${format}. Please try again.`);
    }
    finally {
      this.setState({downloading: false});
    }
  }

  handleDownloadPdf = async () => {
    await this.downloadReport('pdf')
  }

  handleDownloadXlsx = async () => {
    await this.downloadReport('xlsx');
  }

  handleRunReport = (report, parameters) => {
    this.toggleParameters();
    this.props.setUrlState(parameters);
  }

  renderReport() {
    const {user, urlState} = this.props;
    const {report, catalog, loading, error, optionsOpen, downloading, editParameters, downloadMenuOpen} = this.state;

    if (loading) {
      return <Loading message="Running report..." />;
    }

    if (error) {
      return <ErrorMessage message="Report failed to run!" />;
    }

    return (
      <>
        <Content>
          <Card>
            <Card.Toolbar
              start={<ReportTitle>{report.name}</ReportTitle>}
              end={
                <>
                  <Button.Group>
                    <Button id="download-options" onClick={this.toggleDownloads} disabled={downloading}>
                      {downloading ? 'Downloading...' : 'Download Report'}
                    </Button>
                    {(report.parameters.length > 0 || report.published || (!report.published && Role.ADMIN) ||
                        (report.published && Role.MANAGER)) && (
                      <Button id="report-options" onClick={this.toggleReportOptions}>
                        <Icon name="chevron-down" />
                      </Button>
                    )}
                  </Button.Group>
                  <Menu 
                    open={downloadMenuOpen}
                    position="bottom-end"
                    relativeTo="download-options"
                    onClose={this.toggleDownloads}>
                      <Menu.Option onClick={this.handleDownloadPdf}>As PDF</Menu.Option>
                      <Menu.Option onClick={this.handleDownloadXlsx}>As XLSX</Menu.Option>
                  </Menu>
                  <Menu
                    open={optionsOpen}
                    position="bottom-end"
                    relativeTo="report-options" 
                    onClose={this.toggleReportOptions}>
                    {report.parameters.length > 0 && (
                      <Menu.Option onClick={this.toggleParameters}>Edit Parameters</Menu.Option>
                    )}
                    {report.published && (
                      <Menu.Option to={`/reports/schedules?report=${report.id}`}>View Schedules</Menu.Option>
                    )}
                    {!report.published && user.role === Role.ADMIN && (
                      <Menu.Option onClick={() => console.log('publish it')}>Publish Report</Menu.Option>
                    )}
                  </Menu>
                </>
              }
            />
            <Card.Content>
              <ReportViewer ref={this.viewer} />
            </Card.Content>
          </Card>
        </Content>
        <ReportParametersModal
          catalog={catalog}
          report={editParameters && report}
          parameters={urlState}
          onRun={this.handleRunReport}
          onClose={this.toggleParameters}
        />
      </>
    );
  }

  render() {
    return (
      <Screen
        title="Reports"
        activeLink="Reports"
        tabs={
          <Tabs>
            <Tabs.Tab active to="/reports">Reports</Tabs.Tab>
            <Tabs.Tab to="/reports/schedules">Schedules</Tabs.Tab>
          </Tabs>
        }>
        {this.renderReport()}
      </Screen>
    );
  }
}

const ReportTitle = styled(Card.Title)`
  h4 {
    padding: 0;
  }
`;

const ReportViewer = styled.div`
  overflow-x: auto;
`;

export default withUrlState({})(withUser(ReportViewerScreen));